// CustomerGames.jsx — "Show Customer Game Card" drill-down from Total Customers

import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableChartIcon from '@mui/icons-material/TableChart';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import DownloadIcon from '@mui/icons-material/Download';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/constants';
// [CASH-MODE] UPI / Cash filter + indication
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeLabel, paymentModeParam, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import CustomerReportShell, {
  EmptyState,
  ReportGrid,
  SectionCard,
  StatusChip,
  displayVpa,
  formatINR,
  formatISTDate,
  useCanViewCustomerSensitive,
} from '../../components/CustomerReportShell';

const STATUS_TONE = { active: 'warning', used: 'success', expired: 'default' };

const CustomerGames = () => {
  const [games, setGames] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'used' | 'expired' | 'all'
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const canViewSensitive = useCanViewCustomerSensitive();

  const { customerId } = useParams();

  useEffect(() => {
    fetchGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, paymentMode]); // [CASH-MODE] refetch on mode change

  const fetchGames = async () => {
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/playGames/${customerId}/games`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        params: { paymentMode: paymentModeParam(paymentMode) }, // [CASH-MODE] undefined for ALL
      });
      const data = response.data;
      const rawList = Array.isArray(data.games) ? data.games : (Array.isArray(data) ? data : []);
      setGames(rawList.map((item, index) => {
        const rawSt = String(item.status || '').toLowerCase();
        let status = 'active';
        let statusLabel = 'Active';
        if (rawSt === 'used') {
          status = 'used';
          statusLabel = 'Used';
        } else if (rawSt === 'deactive' || rawSt === 'expired') {
          status = 'expired';
          statusLabel = 'Expired';
        }
        return {
          id: item._id || item.id || index + 1,
          serialNumber: index + 1,
          vpaId: item.vpaId || 'N/A',
          transactionAmount: parseFloat(item.totalAmount || item.transactionAmount || 0),
          offerAmount: parseFloat(item.offerAmount || 0),
          offerPercentage: item.offerPercentage || 0,
          randomOffers: item.randomOffers || [],
          date: item.createdAt ? formatISTDate(item.createdAt) : (item.date || '-'),
          expiryDate: item.expiryDate ? formatISTDate(item.expiryDate) : '',
          status,
          statusLabel,
          rawStatus: item.status,
          paymentMode: item.paymentMode, // [CASH-MODE]
        };
      }));
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setGames([]);
      } else {
        setError((err.response && err.response.data && err.response.data.message) || 'An error occurred while fetching game cards.');
        setGames([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // [CASH-MODE] client-side safety net in case the API ignores paymentMode (ALL = unchanged)
  const modeGames = useMemo(() => filterRowsByMode(games, paymentMode), [games, paymentMode]);
  const filteredGames = useMemo(
    () => (statusFilter === 'all' ? modeGames : modeGames.filter((g) => g.status === statusFilter)),
    [modeGames, statusFilter]
  );

  const countOf = (st) => modeGames.filter((g) => g.status === st).length;
  const usedGames = modeGames.filter((g) => g.status === 'used');
  const rewardsWon = usedGames.reduce((s, g) => s + g.offerAmount, 0);
  const txnModeTotals = totalsByModeFromRows(modeGames, (g) => g.transactionAmount);

  const handleExportCSV = () => {
    if (filteredGames.length === 0) {
      setDialogMessage('No data available to export.');
      setOpenDialog(true);
      return;
    }
    const headers = ['S.No.', 'VPA ID', 'Transaction Amount', 'Offer Amount', 'Date', 'Status', 'Mode']; // [CASH-MODE]
    const csvRows = [headers.join(',')];
    filteredGames.forEach((game) => {
      csvRows.push([
        game.serialNumber,
        `"${displayVpa(game.vpaId, canViewSensitive)}"`,
        `"${game.transactionAmount.toFixed(2)}"`,
        `"${game.offerAmount.toFixed(2)}"`,
        `"${game.date}"`,
        `"${game.statusLabel}"`,
        `"${paymentModeLabel(game)}"`, // [CASH-MODE]
      ].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `customer_games_${statusFilter}_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const columns = [
    { field: 'serialNumber', headerName: '#', width: 60 },
    { field: 'date', headerName: 'Date', width: 130 },
    { field: 'transactionAmount', headerName: 'Bill Amount', width: 140, type: 'number', valueFormatter: (v) => formatINR(v) },
    {
      field: 'offerAmount', headerName: 'Reward Won', width: 140, type: 'number',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontWeight: 800, color: p.row.status === 'used' ? 'success.dark' : '#94a3b8', width: '100%', textAlign: 'right' }}>
          {p.row.status === 'active' ? '-' : formatINR(p.value)}
        </Typography>
      ),
    },
    { field: 'offerPercentage', headerName: 'Cashback %', width: 110, valueFormatter: (v) => (v ? `${v}%` : '-') },
    { field: 'statusLabel', headerName: 'Status', width: 120, renderCell: (p) => <StatusChip label={p.value} tone={STATUS_TONE[p.row.status]} /> },
    // [CASH-MODE] UPI / Cash badge
    { field: 'paymentMode', headerName: 'Mode', width: 100, renderCell: (params) => <PaymentModeChip row={params.row} /> },
    { field: 'expiryDate', headerName: 'Expiry', width: 130, valueFormatter: (v) => v || '-' },
    { field: 'vpaId', headerName: 'VPA', flex: 1, minWidth: 180, valueGetter: (v) => displayVpa(v, canViewSensitive) },
  ];

  const tint = { active: ['#fff7e6', '#fde3b0', '#b45309'], used: ['#ecfdf5', '#a7f3d0', '#047857'], expired: ['#f8fafc', '#e2e8f0', '#64748b'] };

  const renderCards = () => (
    <Grid container spacing={2}>
      {filteredGames.map((game) => {
        const [bg, border, fg] = tint[game.status];
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={game.id}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%', transition: 'box-shadow .2s', '&:hover': { boxShadow: '0 8px 20px rgba(15,23,42,0.08)' } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f1f5f9' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>Card #{game.serialNumber}</Typography>
                <Stack direction="row" spacing={0.5}>
                  <PaymentModeChip row={game} />{/* [CASH-MODE] */}
                  <StatusChip label={game.statusLabel} tone={STATUS_TONE[game.status]} />
                </Stack>
              </Stack>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ textAlign: 'center', py: 1.75, borderRadius: 2.5, bgcolor: bg, border: `1px solid ${border}` }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, color: fg }}>
                    {game.status === 'active' ? 'NOT PLAYED YET' : game.status === 'used' ? 'REWARD WON' : 'EXPIRED'}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: fg }}>
                    {game.status === 'active' ? 'Pending' : formatINR(game.offerAmount)}
                  </Typography>
                  {game.status !== 'active' && game.offerPercentage > 0 && (
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>{game.offerPercentage}% cashback</Typography>
                  )}
                </Box>
                <Stack spacing={0.75} sx={{ mt: 1.75 }}>
                  {[
                    ['Bill Amount', formatINR(game.transactionAmount)],
                    ['Date', game.date],
                    ...(game.expiryDate ? [['Expiry', game.expiryDate]] : []),
                    ['VPA', displayVpa(game.vpaId, canViewSensitive)],
                  ].map(([k, v]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{k}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a', textAlign: 'right', wordBreak: 'break-all' }}>{v}</Typography>
                    </Stack>
                  ))}
                </Stack>
                {/* Card offers pool — hidden for active cards */}
                {game.status !== 'active' && game.randomOffers.length > 0 && (
                  <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px dashed #cbd5e1' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                      Offers pool ({game.randomOffers.length} slots)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {game.randomOffers.map((offer, idx) => (
                        <Chip
                          key={idx}
                          label={`${offer}%`}
                          size="small"
                          color={offer === game.offerPercentage ? 'primary' : offer === 100 ? 'warning' : 'default'}
                          variant={offer === game.offerPercentage ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.68rem', height: 22, fontWeight: offer === game.offerPercentage ? 800 : 500 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <CustomerReportShell
      title="Customer Game Cards"
      subtitle="Scratch cards issued on payments, and the rewards won"
      icon={<SportsEsportsIcon />}
      customerId={customerId}
      actions={(
        <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={handleExportCSV} disabled={filteredGames.length === 0} sx={{ fontWeight: 700 }}>
          Export CSV
        </Button>
      )}
      filters={(
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <ToggleButtonGroup value={statusFilter} exclusive onChange={(e, v) => v !== null && setStatusFilter(v)} size="small" color="primary" sx={{ flexWrap: 'wrap' }}>
            <ToggleButton value="active" sx={{ fontWeight: 700, px: 2 }}>Active ({countOf('active')})</ToggleButton>
            <ToggleButton value="used" sx={{ fontWeight: 700, px: 2 }}>Used ({countOf('used')})</ToggleButton>
            <ToggleButton value="expired" sx={{ fontWeight: 700, px: 2 }}>Expired ({countOf('expired')})</ToggleButton>
            <ToggleButton value="all" sx={{ fontWeight: 700, px: 2 }}>All ({modeGames.length})</ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {/* [CASH-MODE] */}
            <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} />
            <ToggleButtonGroup value={viewMode} exclusive onChange={(e, next) => next && setViewMode(next)} size="small" color="primary">
              <ToggleButton value="cards" sx={{ fontWeight: 700, px: 1.5 }}><ViewModuleIcon sx={{ mr: 0.5, fontSize: 18 }} /> Cards</ToggleButton>
              <ToggleButton value="table" sx={{ fontWeight: 700, px: 1.5 }}><TableChartIcon sx={{ mr: 0.5, fontSize: 18 }} /> Table</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      )}
      error={error}
      onRetry={fetchGames}
      statsLoading={loading}
      stats={[
        { label: 'Total Cards', value: modeGames.length, hint: `UPI ${formatINR(txnModeTotals.UPI.amount)} · Cash ${formatINR(txnModeTotals.CASH.amount)} billed`, color: 'primary', icon: <SportsEsportsIcon /> },
        { label: 'Active', value: countOf('active'), hint: 'Not played yet', color: 'warning', icon: <HourglassTopIcon /> },
        { label: 'Used', value: countOf('used'), hint: 'Played', color: 'success', icon: <TaskAltIcon /> },
        { label: 'Expired', value: countOf('expired'), hint: 'Not played in time', color: 'error', icon: <EventBusyIcon /> },
        { label: 'Rewards Won', value: formatINR(rewardsWon), hint: `From ${usedGames.length} used card${usedGames.length === 1 ? '' : 's'}`, color: 'secondary', icon: <CardGiftcardIcon /> },
      ]}
    >
      <SectionCard
        title={`${statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Game Cards`}
        subtitle={`${filteredGames.length} card${filteredGames.length === 1 ? '' : 's'}`}
      >
        {viewMode === 'table' ? (
          <ReportGrid
            rows={filteredGames}
            columns={columns}
            loading={loading}
            rowHeight={56}
            emptyTitle="No game cards found"
            emptyMessage="This customer has no game cards under the selected filters."
          />
        ) : loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
        ) : filteredGames.length === 0 ? (
          <EmptyState
            icon={<SportsEsportsIcon sx={{ fontSize: 52 }} />}
            title={`No ${statusFilter !== 'all' ? statusFilter : ''} game cards found`}
            message="This customer has no game cards under the selected filters."
          />
        ) : renderCards()}
      </SectionCard>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Alert</DialogTitle>
        <DialogContent><Typography variant="body1">{dialogMessage}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary" variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </CustomerReportShell>
  );
};

export default CustomerGames;
